import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, Container, Spinner } from 'react-bootstrap';
import { useQuery, useMutation } from '@apollo/client';
import { ReactComponent as CheckmarkIcon } from '../../../shared/icons/form-icon-valid.svg';
import { graphqlErrorsToUiErrors, queries, mutations } from '../../../api';
import LoadingSpinner from '../../../components/loading-spinner';
import NotFound from '../../not-found';
import ErrorMessage from '../../../components/error-message';
import { TitleFormattedMessage } from '../../../components/titleFormattedMessage';
import PropdocsModal from '../../../components/modal/PropdocsModal';
import styles from './styles.module.scss';

const UPGRADE_SUBSCRIPTION_MESSAGE =
  'Upgrade to access a full suite of tools to draft, edit, negotiate and sign your agreements.';

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const SubscriptionRequiredModal = ({ isOpen, onClose }: ModalProps) => {
  const { loading: portalDataLoading, data: portalData, error: portalDataError } = useQuery(queries.getPortalUrl);
  const [ensureStripeSetup, { loading: setupLoading }] = useMutation(mutations.ensureStripeSetup);
  const [upgradeError, setUpgradeError] = useState<string | null>(null);

  const handleUpgrade = async () => {
    setUpgradeError(null);
    
    // If we already have a portal URL, use it directly
    if (portalData?.currentUser?.portalUrl) {
      window.location.href = portalData.currentUser.portalUrl;
      return;
    }
    
    // Otherwise, ensure Stripe setup and get portal URL
    try {
      const { data } = await ensureStripeSetup();
      if (data?.ensureStripeSetup?.portalUrl) {
        window.location.href = data.ensureStripeSetup.portalUrl;
      } else {
        setUpgradeError('Unable to access upgrade portal. Please try again or contact support.');
      }
    } catch (error) {
      console.error('Failed to ensure Stripe setup:', error);
      setUpgradeError('Failed to setup upgrade portal. Please try again or contact support.');
    }
  };

  if (portalDataLoading) {
    return <LoadingSpinner />;
  }

  if (!portalData && portalDataError) {
    const errors = graphqlErrorsToUiErrors(portalDataError);
    console.warn('ERROR', errors);
    if (errors._general?.id === 'api.error.not-found') {
      return <NotFound />;
    }
    return <ErrorMessage>{errors._general}</ErrorMessage>;
  }
  return (
    <PropdocsModal
      show={isOpen}
      setShow={onClose}
      title={
        <span className={styles.subscribtionRequiredModalHeader}>
          <FormattedMessage id="title.archive-template" defaultMessage="Streamline your deals by upgrading!" />
        </span>
      }
      size="md"
      className={styles.subscribtionRequiredModal}
    >
      <div className={styles.subscribtionRequiredModalContainer}>
        <div className={styles.subscribtionRequiredModalBody}>
          <p>
            <FormattedMessage id="title.archive-template" defaultMessage={UPGRADE_SUBSCRIPTION_MESSAGE} />
          </p>
          <p>
            <CheckmarkIcon height={32} />
            <FormattedMessage id="title.archive-template" defaultMessage="Advanced Document Editor" />
          </p>
          <p>
            <CheckmarkIcon height={32} />
            <FormattedMessage id="title.archive-template" defaultMessage="Internal Approvals" />
          </p>
          <p>
            <CheckmarkIcon height={32} />
            <FormattedMessage id="title.archive-template" defaultMessage="Template Management" />
          </p>
          <p>
            <CheckmarkIcon height={32} />
            <FormattedMessage id="title.archive-template" defaultMessage="eSignatures" />
          </p>
          <p>
            <FormattedMessage id="title.archive-template" defaultMessage="... and much more!" />
          </p>
          <Container className={styles.upgradeButtonContainer}>
            <Button onClick={handleUpgrade} disabled={setupLoading}>
              {setupLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Setting up...
                </>
              ) : (
                <TitleFormattedMessage id="cta.upgrade" defaultMessage="Upgrade" />
              )}
            </Button>
            {upgradeError && (
              <div className="text-danger mt-2">
                {upgradeError}
              </div>
            )}
          </Container>
        </div>
        <div className={styles.subscribtionRequiredModalFooter}>
          <Container>
            Questions? Contact us at
            <a className="ml-1" href="mailto:info@propdocs.com" target="_blank" rel="noopener noreferrer">
              info@propdocs.com
            </a>
          </Container>
        </div>
      </div>
    </PropdocsModal>
  );
};

export default SubscriptionRequiredModal;
